import React, { useState } from 'react';
import { FiX, FiCopy, FiCheckCircle } from 'react-icons/fi';
import {
	parseFieldOptions,
	resolveOptionLabel,
} from '../../../utils/formHelpers';

const ExportModal = ({ isOpen, onClose, submission }) => {
	const [copied, setCopied] = useState(false);

	if (!isOpen || !submission) return null;

	// Format submission data for export
	const formatExportData = () => {
		const lines = [];

		// DEBUG: Log the submission data structure
		console.log('🔍 DEBUG: Full submission object:', submission);
		console.log('🔍 DEBUG: submission.data:', submission.data);
		console.log('🔍 DEBUG: submission.form_fields:', submission.form_fields);
		console.log('🔍 DEBUG: submission.files:', submission.files);

		// Header
		lines.push(`SUBMISSION DETAILS`);
		lines.push(`==================`);
		lines.push('');

		// Basic info
		lines.push(`Form: ${submission.form_name || 'Unknown Form'}`);

		// Fix date formatting - use submitted_at instead of created_at
		const submissionDate = submission.submitted_at || submission.created_at;
		if (submissionDate) {
			try {
				const date = new Date(submissionDate);
				lines.push(
					`Submission Date: ${date.toLocaleDateString('en-US', {
						year: 'numeric',
						month: 'long',
						day: 'numeric',
						hour: '2-digit',
						minute: '2-digit',
					})}`
				);
			} catch (e) {
				lines.push(`Submission Date: ${submissionDate}`);
			}
		}

		lines.push(
			`Status: ${
				submission.status
					? submission.status.charAt(0).toUpperCase() +
					  submission.status.slice(1)
					: 'Submitted'
			}`
		);
		lines.push(`Submission ID: ${submission.id}`);
		lines.push('');

		// Client information - use correct field names and remove email/username
		lines.push(`CLIENT INFORMATION`);
		lines.push(`==================`);

		// Use first_name and last_name from submission
		const firstName = submission.first_name || '';
		const lastName = submission.last_name || '';
		if (firstName || lastName) {
			lines.push(`Name: ${firstName} ${lastName}`.trim());
		}

		lines.push('');

		// Form responses - parse the data field properly
		let formData;
		try {
			formData =
				typeof submission.data === 'string'
					? JSON.parse(submission.data)
					: submission.data || {};
		} catch (e) {
			formData = {};
		}

		console.log('🔍 DEBUG: Parsed formData:', formData);

		// Merge file URLs into formData if available
		if (submission.files && Array.isArray(submission.files)) {
			submission.files.forEach((file) => {
				const currentVal = formData[file.field_key];
				const isUsableUrl =
					typeof currentVal === 'string' && currentVal.startsWith('http');
				if (!isUsableUrl) {
					formData[file.field_key] = file.file_url;
				}
			});
		}

		console.log('🔍 DEBUG: formData after file merge:', formData);

		// Helper: resolve select/radio/multiselect/checkbox values to labels
		const resolveChoiceValue = (field, value) => {
			const choiceTypes = ['select', 'radio', 'multiselect', 'checkbox'];
			if (!choiceTypes.includes(field.type?.toLowerCase())) return null;

			const options = parseFieldOptions(field.options);
			if (Array.isArray(value)) {
				return value
					.map((v) => resolveOptionLabel(options, v))
					.join(', ');
			}
			return resolveOptionLabel(options, value);
		};

		// Render a single field's value as export text lines
		const formatFieldForExport = (field, value) => {
			const fieldLines = [];
			fieldLines.push(`${field.label}:`);

			if (field.type === 'address' && value) {
				if (typeof value === 'string') {
					try {
						const addressData = JSON.parse(value);
						fieldLines.push(
							`  ${
								addressData.place_name ||
								addressData.address ||
								addressData.formatted_address ||
								'Address provided'
							}`
						);
					} catch (e) {
						fieldLines.push(`  ${value}`);
					}
				} else if (typeof value === 'object' && value !== null) {
					const addressText =
						value.place_name ||
						value.address ||
						value.formatted_address ||
						(value.street && value.city
							? `${value.street}, ${value.city}${
									value.state ? `, ${value.state}` : ''
							  }${value.zip ? ` ${value.zip}` : ''}`
							: '') ||
						'Address provided';
					fieldLines.push(`  ${addressText}`);
				} else {
					fieldLines.push(`  ${value}`);
				}
			} else if (field.type === 'file' && value) {
				if (typeof value === 'string' && value.startsWith('http')) {
					fieldLines.push(`  File: ${value}`);
				} else {
					try {
						const fileData = JSON.parse(value);
						if (Array.isArray(fileData)) {
							fileData.forEach((file) => {
								fieldLines.push(
									`  File: ${file.name || 'File uploaded'}`
								);
							});
						} else {
							fieldLines.push(
								`  File: ${fileData.name || 'File uploaded'}`
							);
						}
					} catch (e) {
						fieldLines.push(`  File uploaded`);
					}
				}
			} else if (value !== undefined && value !== null && value !== '') {
				const resolved = resolveChoiceValue(field, value);
				const displayValue =
					resolved !== null ? resolved : value.toString();
				if (displayValue.includes('\n')) {
					fieldLines.push(
						`  ${displayValue.split('\n').join('\n  ')}`
					);
				} else {
					fieldLines.push(`  ${displayValue}`);
				}
			} else {
				fieldLines.push(`  (No response provided)`);
			}

			return fieldLines;
		};

		if (submission.form_fields && submission.form_fields.length > 0) {
			lines.push(`FORM RESPONSES`);
			lines.push(`==============`);

			// Group fields by section
			const fieldsBySection = {};
			const fieldsWithoutSection = [];

			submission.form_fields.forEach((field) => {
				console.log(
					'🔍 DEBUG: Processing field:',
					field.field_key,
					'value:',
					formData[field.field_key]
				);
				if (field.section_title) {
					if (!fieldsBySection[field.section_title]) {
						fieldsBySection[field.section_title] = [];
					}
					fieldsBySection[field.section_title].push(field);
				} else {
					fieldsWithoutSection.push(field);
				}
			});

			console.log('🔍 DEBUG: fieldsWithoutSection:', fieldsWithoutSection);
			console.log('🔍 DEBUG: fieldsBySection:', fieldsBySection);

			// Render fields without section first
			fieldsWithoutSection.forEach((field, index) => {
				const value = formData[field.field_key];
				lines.push(...formatFieldForExport(field, value));

				if (
					index < fieldsWithoutSection.length - 1 ||
					Object.keys(fieldsBySection).length > 0
				) {
					lines.push('');
				}
			});

			// Render sections
			Object.entries(fieldsBySection).forEach(
				([sectionTitle, fields], sectionIndex) => {
					if (fieldsWithoutSection.length > 0 || sectionIndex > 0) {
						lines.push('');
					}

					lines.push(`${sectionTitle.toUpperCase()}`);
					lines.push('='.repeat(sectionTitle.length));

					fields.forEach((field, fieldIndex) => {
						const value = formData[field.field_key];
						lines.push(...formatFieldForExport(field, value));

						if (fieldIndex < fields.length - 1) {
							lines.push('');
						}
					});
				}
			);
		} else {
			console.log('🔍 DEBUG: No form fields or formData is empty');
		}

		return lines.join('\n');
	};

	const exportData = formatExportData();

	const handleCopy = async () => {
		try {
			await navigator.clipboard.writeText(exportData);
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		} catch (err) {
			console.error('Failed to copy text: ', err);
		}
	};

	return (
		<div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
			<div className='bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col'>
				{/* Header */}
				<div className='flex items-center justify-between p-6 border-b'>
					<h2 className='text-xl font-semibold text-gray-900'>
						Export Submission Data
					</h2>
					<button
						onClick={onClose}
						className='text-gray-400 hover:text-gray-600 transition-colors'>
						<FiX size={24} />
					</button>
				</div>

				{/* Content */}
				<div className='flex-1 p-6 overflow-hidden flex flex-col'>
					<p className='text-sm text-gray-600 mb-4'>
						Copy this formatted data to paste into your tasking system:
					</p>

					{/* Preview */}
					<div className='flex-1 bg-gray-50 rounded-lg p-4 overflow-auto border'>
						<pre className='text-sm text-gray-800 whitespace-pre-wrap font-mono leading-relaxed'>
							{exportData}
						</pre>
					</div>
				</div>

				{/* Footer */}
				<div className='flex items-center justify-end gap-3 p-6 border-t bg-gray-50'>
					<button
						onClick={onClose}
						className='px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors'>
						Close
					</button>
					<button
						onClick={handleCopy}
						className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
							copied
								? 'bg-green-100 text-green-700 border border-green-200'
								: 'bg-blue-600 text-white hover:bg-blue-700'
						}`}>
						{copied ? (
							<>
								<FiCheckCircle size={16} />
								Copied!
							</>
						) : (
							<>
								<FiCopy size={16} />
								Copy to Clipboard
							</>
						)}
					</button>
				</div>
			</div>
		</div>
	);
};

export default ExportModal;
